import {
  Banknote,
  Building2,
  ClipboardCheck,
  HardHat,
  ReceiptText,
  TriangleAlert,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { api, rows } from "../../../lib/api";
import type { ApiList } from "../../../lib/types";
import { useBtpCapabilities } from "../access";
import { btpPaths } from "../api";
import type { Chantier, ChantierOverview } from "../types";

function money(value: unknown, currency = "XOF") {
  return `${Number(value || 0).toLocaleString("fr-FR")} ${currency}`;
}

export function BtpDashboardPage() {
  const capabilities = useBtpCapabilities();
  const [chantiers, setChantiers] = useState<Chantier[]>([]);
  const [overviews, setOverviews] = useState<ChantierOverview[]>([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");

  async function load() {
    setBusy(true);
    try {
      const list = rows(await api.get<ApiList<Chantier>>(btpPaths.chantiers));
      setChantiers(list);
      const summaries = await Promise.all(
        list.map((chantier) =>
          api.get<ChantierOverview>(btpPaths.overview(chantier.id)),
        ),
      );
      setOverviews(summaries);
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Chargement impossible.");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const stats = useMemo(() => {
    const actifs = chantiers.filter((c) => c.statut === "ACTIF").length;
    const currencies = [...new Set(chantiers.map(site => site.devise || "XOF"))];
    const total = (key: "contrats" | "execute" | "paye") => currencies.map(currency => {
      const sites = chantiers.filter(site => (site.devise || "XOF") === currency);
      const summaries = overviews.filter(summary => sites.some(site => site.id === summary.chantier));
      const amount = key === "contrats" ? sites.reduce((sum, site) => sum + Number(site.montant_contrat || 0), 0) : summaries.reduce((sum, summary) => sum + Number((key === "execute" ? summary.execute_brut : summary.encaisse_ttc ?? summary.paye_net) || 0), 0);
      return money(amount, currency);
    }).join(" · ") || "0";
    return {actifs, contrats: total("contrats"), execute: total("execute"), paye: total("paye")};
  }, [chantiers, overviews]);

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-amber-700">Vertical métier</div>
          <h1 className="mt-1 text-3xl font-black">{capabilities.profile === "CHANTIER" ? "Mon chantier aujourd’hui" : capabilities.profile === "DIRECTION" ? "Portfolio BTP" : "BTP & Chantiers"}</h1>
          <p className="mt-2 max-w-3xl text-sm text-slate-500">
            Pilotage opérationnel des chantiers, lots, DQE, situations, sous-traitance,
            consommations et rapports terrain.
          </p>
        </div>
        <Link to="/btp/chantiers" className="btn-primary">
          <HardHat size={17} /> Ouvrir les chantiers
        </Link>
      </div>

      {error && <div role="alert" className="mt-4 rounded-xl bg-red-50 p-4 text-sm font-semibold text-red-700">{error}</div>}
      {!error && <div className="mt-7 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[
          { label: "Chantiers actifs", value: stats.actifs, Icon: Building2 },
          { label: "Montant contrats", value: stats.contrats, Icon: Banknote },
          { label: "Travaux exécutés", value: stats.execute, Icon: ClipboardCheck },
          { label: "Encaissé TTC", value: stats.paye, Icon: ReceiptText },
        ].filter(item => capabilities.financial_read || item.label === "Chantiers actifs").map(({ label, value, Icon }) => (
          <div key={label} className="panel p-5">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-500">{label}</span>
              <span className="grid size-9 place-items-center rounded-xl bg-amber-50 text-amber-700">
                <Icon size={18} />
              </span>
            </div>
            <div className="mt-4 text-2xl font-black text-slate-950">{value}</div>
          </div>
        ))}
      </div>}

      <section className="panel mt-6 overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div>
            <h2 className="font-bold">Chantiers récents</h2>
            <p className="mt-1 text-xs text-slate-500">Vue consolidée du terrain et des situations.</p>
          </div>
          {busy && <span className="text-xs text-slate-400">Actualisation…</span>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-5 py-3">Chantier</th>
                <th className="px-5 py-3">Statut</th>
                {capabilities.financial_read && <th className="px-5 py-3">Contrat</th>}
                <th className="px-5 py-3">Avancement</th>
                <th className="px-5 py-3">Situations</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {chantiers.slice(0, 10).map((chantier) => {
                const summary = overviews.find((item) => item.chantier === chantier.id);
                return (
                  <tr key={chantier.id} className="hover:bg-slate-50/70">
                    <td className="px-5 py-4">
                      <div className="font-bold">{chantier.code}</div>
                      <div className="mt-1 text-slate-500">{chantier.titre}</div>
                    </td>
                    <td className="px-5 py-4">{chantier.statut}</td>
                    {capabilities.financial_read && <td className="px-5 py-4 font-semibold">{money(chantier.montant_contrat, chantier.devise)}</td>}
                    <td className="px-5 py-4">{Number(summary?.avancement || 0)} %</td>
                    <td className="px-5 py-4">{summary?.situations ?? 0}</td>
                    <td className="px-5 py-4 text-right">
                      <Link className="btn-secondary" to={`/btp/chantiers/${chantier.id}`}>
                        Ouvrir
                      </Link>
                    </td>
                  </tr>
                );
              })}
              {!busy && !chantiers.length && (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-slate-500">
                    <TriangleAlert className="mx-auto mb-2" size={22} />
                    Aucun chantier pour cette entreprise.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
